/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import $ from 'jquery';

import Currency from '../../data/Currency';
import Platforms from '../../data/Platforms';
import BankCodes from '../../data/BankCodes';
import UpdateModal from '../Modals/UpdateModal';
import IziToast from '../IziToast';
import TextInput from '../TextInput';
import Select from '../Select';
import CheckBox from '../CheckBox';
import FilePreview from '../FileUploader/FilePreview';

const BANK_PLATFORMS = ['co-op', 'ncba'];

const UpdateProperty = ({ requestConfig, reLoadTable, selectedProperty }) => {
  const bankNameRef = React.useRef(null);
  const bankCodeRef = React.useRef(null);

  // Core fields
  const [propertyName, setPropertyName] = useState('');
  const [propertyCode, setPropertyCode] = useState('');
  const [description, setDescription] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [propertyUnits, setPropertyUnits] = useState('');
  const [paymentPlatform, setPaymentPlatform] = useState(Platforms[0].id);
  const [currency, setCurrency] = useState(Currency[0].id);
  const [currentFile, setCurrentFile] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Mpesa / MoMo fields
  const [accountNumber, setAccountNumber] = useState('');
  const [tenantPaymentEmail, setTenantPaymentEmail] = useState('');
  const [tenantPaymentPhone, setTenantPaymentPhone] = useState('');

  // Landlord bank fields
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [bankTelephoneNumber, setBankTelephoneNumber] = useState('');
  const [isEquityAccount, setIsEquityAccount] = useState(false);

  // Bank integration fields
  const [coopAccount, setCoopAccount] = useState('');
  const [coopNarrationId, setCoopNarrationId] = useState('');
  const [ncbaShortcode, setNcbaShortcode] = useState('');

  // Suspense notification settings
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifWhatsapp, setNotifWhatsapp] = useState(false);
  const [notifEmail, setNotifEmail] = useState(false);
  const [notifWhatsappNumber, setNotifWhatsappNumber] = useState('');
  const [notifEmailAddress, setNotifEmailAddress] = useState('');

  const isBankPlatform = BANK_PLATFORMS.includes(paymentPlatform);
  const isCoop = paymentPlatform === 'co-op';
  const isNcba = paymentPlatform === 'ncba';

  // Load selected property into state
  useEffect(() => {
    if (!selectedProperty || !selectedProperty.id) return;

    let currentAccount = '';
    let currentPlatform = Platforms[0].id;
    let currentBankName = '';
    let currentBankAccountNumber = '';
    let currentBankCode = '';
    let currentBankPhone = '';
    let currentTenantPaymentEmail = '';
    let currentTenantPaymentPhone = '';
    let currentBankIsEquityAccount = false;

    if (selectedProperty.tenant_payment_details
      && selectedProperty.tenant_payment_details[0]
      && selectedProperty.tenant_payment_details[0].payment_info) {
      const info = selectedProperty.tenant_payment_details[0].payment_info;
      currentPlatform = info.platform || Platforms[0].id;
      currentAccount = info.account || '';
      currentTenantPaymentEmail = info.email || '';
      currentTenantPaymentPhone = info.phone || '';
    }

    if (selectedProperty.landlord_payment_details
      && selectedProperty.landlord_payment_details[0]
      && selectedProperty.landlord_payment_details[0].payment_info) {
      const info = selectedProperty.landlord_payment_details[0].payment_info;
      currentBankName = info.bank_name || '';
      currentBankAccountNumber = info.account || '';
      currentBankCode = info.code || '';
      currentBankPhone = info.phone || '';
      currentBankIsEquityAccount = info.is_equity_account || false;
    }

    setPropertyName(selectedProperty.name || '');
    setPropertyCode(selectedProperty.property_code || '');
    setDescription(selectedProperty.description || '');
    setLocationAddress(selectedProperty.address || '');
    setPropertyUnits(selectedProperty.units || '');
    setPaymentPlatform(currentPlatform);
    setCurrency(
      (selectedProperty.other_details && selectedProperty.other_details.currency) || Currency[0].id,
    );
    setAccountNumber(currentAccount);
    setBankAccountName(currentBankName);
    setBankAccountNumber(currentBankAccountNumber);
    setBankCode(currentBankCode);
    setBankTelephoneNumber(currentBankPhone);
    setIsEquityAccount(currentBankIsEquityAccount);
    setTenantPaymentEmail(currentTenantPaymentEmail);
    setTenantPaymentPhone(currentTenantPaymentPhone);

    if (selectedProperty.other_details && selectedProperty.other_details.bank_integrations) {
      const { bank_integrations } = selectedProperty.other_details;
      setCoopAccount(bank_integrations['co-op'] || '');
      setCoopNarrationId(bank_integrations['co-op_narration'] || '');
      setNcbaShortcode(bank_integrations.ncba || '');
    } else {
      setCoopAccount('');
      setCoopNarrationId('');
      setNcbaShortcode('');
    }

    // Load suspense notification config
    const sn = selectedProperty.other_details && selectedProperty.other_details.suspense_notifications;
    if (sn && sn.enabled) {
      setNotifEnabled(true);
      setNotifWhatsapp((sn.channels || []).includes('whatsapp'));
      setNotifEmail((sn.channels || []).includes('email'));
      setNotifWhatsappNumber(sn.whatsapp_number || '');
      setNotifEmailAddress(sn.email || '');
    } else {
      setNotifEnabled(false);
      setNotifWhatsapp(false);
      setNotifEmail(false);
      setNotifWhatsappNumber('');
      setNotifEmailAddress('');
    }

    if (selectedProperty.images && selectedProperty.images.length) {
      setCurrentFile(selectedProperty.images[0].link);
    }
  }, [selectedProperty]);

  const updateProperty = async () => {
    setIsSubmitting(true);

    const baseValid = propertyName && propertyCode && description
      && propertyUnits && locationAddress && currency;

    let platformValid = false;
    if (isCoop) {
      platformValid = !!coopAccount;
    } else if (isNcba) {
      platformValid = !!ncbaShortcode;
    } else {
      platformValid = accountNumber
        && bankAccountName && bankAccountNumber && bankCode && bankTelephoneNumber
        && (paymentPlatform === 'momo' ? tenantPaymentPhone && tenantPaymentEmail : true);
    }

    if (!baseValid || !platformValid) {
      IziToast('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    const otherDetails = { currency };
    let tenantPaymentInfo;
    let landlordPaymentInfo;

    // Helper to build notification config
    const buildNotifConfig = () => {
      if (!notifEnabled) return null;
      const channels = [...(notifWhatsapp ? ['whatsapp'] : []), ...(notifEmail ? ['email'] : [])];
      return {
        enabled: true,
        channels,
        whatsapp_number: notifWhatsapp ? notifWhatsappNumber.trim() : null,
        email: notifEmail ? notifEmailAddress.trim() : null,
      };
    };

    if (isCoop) {
      otherDetails.bank_integrations = { 'co-op': coopAccount.trim() };
      if (coopNarrationId.trim()) {
        otherDetails.bank_integrations['co-op_narration'] = coopNarrationId.trim();
      }
      const notifCfg = buildNotifConfig();
      if (notifCfg) otherDetails.suspense_notifications = notifCfg;
      tenantPaymentInfo = [{
        payment_info: { account: coopAccount.trim(), platform: 'co-op' },
        payment_type: 'rent',
      }];
      landlordPaymentInfo = [];
    } else if (isNcba) {
      otherDetails.bank_integrations = { ncba: ncbaShortcode.trim() };
      const notifCfg = buildNotifConfig();
      if (notifCfg) otherDetails.suspense_notifications = notifCfg;
      tenantPaymentInfo = [{
        payment_info: { account: ncbaShortcode.trim(), platform: 'ncba' },
        payment_type: 'rent',
      }];
      landlordPaymentInfo = [];
    } else {
      tenantPaymentInfo = [{
        payment_info: paymentPlatform === 'momo'
          ? { account: accountNumber, platform: paymentPlatform, phone: tenantPaymentPhone, email: tenantPaymentEmail }
          : { account: accountNumber, platform: paymentPlatform },
        payment_type: 'rent',
      }];
      landlordPaymentInfo = [{
        payment_info: {
          account: bankAccountNumber,
          platform: 'pesalink',
          bank_name: bankAccountName,
          code: bankCode,
          phone: bankTelephoneNumber,
          is_equity_account: isEquityAccount,
        },
        payment_type: 'rent',
      }];
    }

    const formData = new FormData();
    formData.append('id', selectedProperty.id);
    formData.append('name', propertyName);
    formData.append('property_code', propertyCode);
    formData.append('description', description);
    formData.append('address', locationAddress);
    formData.append('units', propertyUnits);
    formData.append('other_details', JSON.stringify(otherDetails));
    formData.append('tenant_payment_details', JSON.stringify(tenantPaymentInfo));
    formData.append('landlord_payment_details', JSON.stringify(landlordPaymentInfo));
    if (currentFile && typeof currentFile === 'object') {
      formData.append(currentFile.name, currentFile, currentFile.name);
    }

    IziToast('Updating...');
    try {
      const { data: { response } } = await axios.put(
        `${process.env.REACT_APP_API_URL}properties`,
        formData,
        requestConfig,
      );
      if (response.propertyUpdated.id) {
        IziToast('Property successfully updated');
        $('#propertyModal').modal('hide');
        if (reLoadTable) reLoadTable();
      }
      setIsLoading((prev) => !prev);
    } catch (error) {
      if (error.response && error.response.data) {
        IziToast(error.response.data.error);
      } else {
        IziToast(error.message || 'An error occurred');
      }
    }
    setIsSubmitting(false);
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (['image/jpeg', 'image/png'].includes(file.type)) {
      setCurrentFile(file);
    } else {
      IziToast('Only .jpeg and .png files allowed');
    }
  };

  return (
    <UpdateModal modalId="propertyModal" onClick={updateProperty} header="Update property details">
      <div className="row">
        <div className="col-xs-3 bm-responsive-div">
          <div className="row">
            <FilePreview
              currentFile={currentFile}
              onChange={onFileChange}
              inputId="imgUpdateProperty"
              onFileRemove={() => setCurrentFile('')}
              columnWidth={12}
            />
          </div>
        </div>

        <div className="col-xs-9 bm-responsive-div">

          {/* ── Basic property info ─────────────────────────────────── */}
          <div className="row">
            <TextInput
              type="text"
              placeholder="Enter property name"
              caption="Property Name"
              onChange={(e) => setPropertyName(e.target.value)}
              value={propertyName}
              columnWidth={12}
              className={isSubmitting && !propertyName ? 'error' : ''}
            />
          </div>
          <div className="row">
            <TextInput
              type="text"
              placeholder="Enter property code"
              caption="Property Code"
              onChange={(e) => setPropertyCode(e.target.value)}
              value={propertyCode}
              columnWidth={6}
              className={isSubmitting && !propertyCode ? 'error' : ''}
            />
            <TextInput
              type="text"
              placeholder="Enter units"
              caption="Units"
              onChange={(e) => {
                if (e.target.value === '' || /^[0-9\b]+$/.test(e.target.value)) {
                  setPropertyUnits(e.target.value);
                }
              }}
              value={propertyUnits}
              columnWidth={6}
              className={isSubmitting && !propertyUnits ? 'error' : ''}
            />
          </div>
          <div className="row">
            <TextInput
              type="text"
              placeholder="Enter property description"
              caption="Property Description"
              onChange={(e) => setDescription(e.target.value)}
              value={description}
              columnWidth={12}
              className={isSubmitting && !description ? 'error' : ''}
            />
          </div>
          <div className="row">
            <TextInput
              type="text"
              placeholder="Enter area, town"
              caption="Area, Town"
              onChange={(e) => setLocationAddress(e.target.value)}
              value={locationAddress}
              columnWidth={12}
              className={isSubmitting && !locationAddress ? 'error' : ''}
            />
          </div>

          <br />

          {/* ── Platform + Currency ─────────────────────────────────── */}
          <div className="row">
            <Select
              caption="Payment Platform"
              value={paymentPlatform}
              onChange={(e) => setPaymentPlatform(e.target.value)}
              columnWidth={6}
              className={isSubmitting && !paymentPlatform ? 'error' : ''}
            >
              {Platforms.map((item) => (
                <option value={item.id} key={item.id}>{item.name}</option>
              ))}
            </Select>
            <Select
              caption="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              columnWidth={6}
              className={isSubmitting && !currency ? 'error' : ''}
            >
              {Currency.map((item) => {
                if (isBankPlatform || paymentPlatform === 'mpesa_paybill') {
                  return item.id === 'KES'
                    ? <option value={item.id} key={item.id}>{item.name}</option>
                    : null;
                }
                if (paymentPlatform === 'momo') {
                  return item.id !== 'KES'
                    ? <option value={item.id} key={item.id}>{item.name}</option>
                    : null;
                }
                return null;
              })}
            </Select>
          </div>

          {/* ── CO-OP: only account number ─────────────────────────── */}
          {isCoop && (
            <div className="row">
              <div className="col-md-12">
                <div
                  className="alert"
                  style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, padding: '10px 14px', marginBottom: 10 }}
                >
                  <i className="fa fa-info-circle" style={{ marginRight: 6, color: '#f39c12' }} />
                  Co-op Bank payments are automatically routed to this property using the account number below.
                </div>
              </div>
              <TextInput
                type="text"
                placeholder="e.g. 01143650439100"
                caption="Co-op Bank Account Number"
                onChange={(e) => setCoopAccount(e.target.value)}
                value={coopAccount}
                columnWidth={6}
                className={isSubmitting && !coopAccount ? 'error' : ''}
              />
              <TextInput
                type="text"
                placeholder="e.g. 1834987"
                caption="Co-op Narration ID (optional)"
                onChange={(e) => setCoopNarrationId(e.target.value)}
                value={coopNarrationId}
                columnWidth={6}
              />
              <div className="col-md-12">
                <small style={{ color: '#777', fontStyle: 'italic' }}>
                  The fixed prefix that appears in your Co-op narration before the unit number
                  (e.g. <code>1834987</code> from <code>1834987#G4</code>). When set, payments are
                  routed by narration first — the suffix maps to the tenant&apos;s unit number.
                </small>
              </div>
            </div>
          )}

          {/* ── NCBA: only shortcode ────────────────────────────────── */}
          {isNcba && (
            <div className="row">
              <div className="col-md-12">
                <div
                  className="alert"
                  style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, padding: '10px 14px', marginBottom: 10 }}
                >
                  <i className="fa fa-info-circle" style={{ marginRight: 6, color: '#f39c12' }} />
                  NCBA payments are routed using the Property Reference (BillRefNumber)
                  below. Tenants enter their unit number in the Narrative when paying.
                </div>
              </div>
              <TextInput
                type="text"
                placeholder="e.g. 132228"
                caption="NCBA Property Reference (BillRefNumber)"
                onChange={(e) => setNcbaShortcode(e.target.value)}
                value={ncbaShortcode}
                columnWidth={6}
                className={isSubmitting && !ncbaShortcode ? 'error' : ''}
              />
            </div>
          )}

          {/* ── Suspense notification settings (co-op / ncba only) ─── */}
          {isBankPlatform && (
            <div className="row" style={{ marginTop: 8 }}>
              <div className="col-md-12">
                <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: '16px 20px', background: '#fafafa' }}>
                  <label className="control-label" style={{ fontSize: 14, marginBottom: 10, display: 'block' }}>
                    <i className="fa fa-bell" style={{ marginRight: 6, color: '#c0392b' }} />
                    Suspense Payment Notifications
                    <small style={{ fontWeight: 'normal', marginLeft: 8, color: '#777' }}>
                      — notify when a payment cannot be automatically reconciled
                    </small>
                  </label>

                  {/* Enable toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <span style={{ fontSize: 13, color: '#555' }}>Enable notifications</span>
                    <div
                      onClick={() => setNotifEnabled((v) => !v)}
                      style={{
                        cursor: 'pointer',
                        width: 48, height: 26, borderRadius: 13,
                        background: notifEnabled ? '#c0392b' : '#ccc',
                        position: 'relative', transition: 'background 0.2s',
                      }}
                    >
                      <div style={{
                        position: 'absolute', top: 3,
                        left: notifEnabled ? 25 : 3,
                        width: 20, height: 20, borderRadius: '50%',
                        background: '#fff', transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,.3)',
                      }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 'bold', color: notifEnabled ? '#c0392b' : '#999' }}>
                      {notifEnabled ? 'ON' : 'OFF'}
                    </span>
                  </div>

                  {notifEnabled && (
                    <>
                      {/* Channel checkboxes */}
                      <div style={{ marginBottom: 14 }}>
                        <label className="control-label" style={{ fontSize: 13 }}>Notification Channel</label>
                        <div style={{ display: 'flex', gap: 24, marginTop: 6 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={notifWhatsapp}
                              onChange={(e) => setNotifWhatsapp(e.target.checked)}
                            />
                            <i className="fa fa-whatsapp" style={{ color: '#25D366', fontSize: 16 }} />
                            WhatsApp
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={notifEmail}
                              onChange={(e) => setNotifEmail(e.target.checked)}
                            />
                            <i className="fa fa-envelope" style={{ color: '#3498db', fontSize: 14 }} />
                            Email
                          </label>
                        </div>
                      </div>

                      {notifWhatsapp && (
                        <div className="row">
                          <TextInput
                            type="text"
                            placeholder="e.g. +254712345678 (include country code)"
                            caption="WhatsApp Number"
                            onChange={(e) => setNotifWhatsappNumber(e.target.value)}
                            value={notifWhatsappNumber}
                            columnWidth={6}
                          />
                        </div>
                      )}

                      {notifEmail && (
                        <div className="row">
                          <TextInput
                            type="email"
                            placeholder="e.g. landlord@example.com"
                            caption="Notification Email"
                            onChange={(e) => setNotifEmailAddress(e.target.value)}
                            value={notifEmailAddress}
                            columnWidth={6}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── MPESA / MOMO: paybill + landlord bank details ──────── */}
          {!isBankPlatform && (
            <>
              <div className="row">
                <TextInput
                  type="text"
                  placeholder="Enter paybill number"
                  caption="Paybill Number"
                  onChange={(e) => {
                    if (e.target.value === '' || /^[0-9\b]+$/.test(e.target.value)) {
                      setAccountNumber(e.target.value);
                    }
                  }}
                  value={accountNumber}
                  columnWidth={6}
                  className={isSubmitting && !accountNumber ? 'error' : ''}
                />
                {paymentPlatform === 'momo' && (
                  <TextInput
                    type="email"
                    placeholder="Enter email"
                    caption="Linked Email Address"
                    onChange={(e) => setTenantPaymentEmail(e.target.value)}
                    value={tenantPaymentEmail}
                    columnWidth={6}
                    className={isSubmitting && !tenantPaymentEmail ? 'error' : ''}
                  />
                )}
                {paymentPlatform === 'momo' && (
                  <TextInput
                    type="text"
                    placeholder="Enter phone number"
                    caption="Linked Telephone Number"
                    onChange={(e) => setTenantPaymentPhone(e.target.value)}
                    value={tenantPaymentPhone}
                    columnWidth={6}
                    className={isSubmitting && !tenantPaymentPhone ? 'error' : ''}
                  />
                )}
              </div>
              <div className="row">
                <Select
                  caption="Enter bank account name"
                  value={bankAccountName}
                  onChange={(e) => setBankAccountName(e.target.value)}
                  columnWidth={6}
                  className={isSubmitting && !bankAccountName ? 'error' : ''}
                  inputRef={bankNameRef}
                >
                  <option value="">Select bank account name</option>
                  {BankCodes.map((item) => (
                    <option value={item.name} key={item.name}>{item.name}</option>
                  ))}
                </Select>
                <Select
                  caption="Bank Code"
                  value={bankCode}
                  onChange={(e) => setBankCode(e.target.value)}
                  columnWidth={6}
                  className={isSubmitting && !bankCode ? 'error' : ''}
                  inputRef={bankCodeRef}
                >
                  <option value="">Select bank code</option>
                  {BankCodes.map((item) => (
                    <option value={item.code} key={item.code}>
                      {`${item.code} - ${item.name}`}
                    </option>
                  ))}
                </Select>
                <TextInput
                  type="text"
                  placeholder="Enter bank account number"
                  caption="Bank Account Number"
                  onChange={(e) => {
                    if (e.target.value === '' || /^[0-9\b]+$/.test(e.target.value)) {
                      setBankAccountNumber(e.target.value);
                    }
                  }}
                  value={bankAccountNumber}
                  columnWidth={6}
                  className={isSubmitting && !bankAccountNumber ? 'error' : ''}
                />
                <TextInput
                  type="text"
                  placeholder="Enter telephone number linked to account"
                  caption="Linked Telephone Number"
                  onChange={(e) => setBankTelephoneNumber(e.target.value)}
                  value={bankTelephoneNumber}
                  columnWidth={6}
                  className={isSubmitting && !bankTelephoneNumber ? 'error' : ''}
                />
                <div className="col-md-6">
                  <label className="control-label" htmlFor="">
                    Is it an Equity Bank account?
                    <div className="row" style={{ marginLeft: '3px', paddingTop: '10px' }}>
                      <CheckBox
                        caption="YES"
                        checked={isEquityAccount}
                        onClick={() => setIsEquityAccount(true)}
                      />
                      <CheckBox
                        caption="NO"
                        checked={!isEquityAccount}
                        onClick={() => setIsEquityAccount(false)}
                      />
                    </div>
                  </label>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </UpdateModal>
  );
};

UpdateProperty.propTypes = {
  requestConfig: PropTypes.objectOf(PropTypes.object),
  selectedProperty: PropTypes.oneOfType([PropTypes.object]),
  reLoadTable: PropTypes.func.isRequired,
};

UpdateProperty.defaultProps = {
  requestConfig: {},
  selectedProperty: {},
};

export default UpdateProperty;
